import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCallback, useEffect, useState } from 'react';
import { http } from '../../api/http';
import { ItemFormDialog } from './ItemFormDialog';

export interface Item {
  _id: string;
  title: string;
  description: string;
  slug: string;
  isActive: boolean;
  createdAt: string;
  categoryId?: string;
  images?: string[];
  order?: number;
}

interface Category {
  _id: string;
  name: string;
}

interface ItemsResponse {
  success: boolean;
  data: Item[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const ItemsPage = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Item | null>(null);

  const fetchItems = useCallback(
    async (pageIndex = page, limit = rowsPerPage, category?: string) => {
      setLoading(true);
      setError(null);
      try {
        const res = await http.get<ItemsResponse>('/items', {
          params: {
            page: pageIndex + 1,
            limit,
            ...(category && { category })
          }
        });
        setItems(res.data.data);
        setTotal(res.data.meta.total);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load items');
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage]
  );

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await http.get<{ success: boolean; data: Category[] }>('/categories');
        setCategories(res.data.data);
      } catch {
        setCategories([]);
      }
    };
    void loadCategories();
  }, []);

  useEffect(() => {
    void fetchItems(page, rowsPerPage, categoryFilter || undefined);
  }, [page, rowsPerPage, categoryFilter, fetchItems]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setOpenDialog(true);
  };

  const handleEdit = (item: Item) => {
    setEditingItem(item);
    setOpenDialog(true);
  };

  const handleDialogClose = (refresh?: boolean) => {
    setOpenDialog(false);
    setEditingItem(null);
    if (refresh) {
      void fetchItems(page, rowsPerPage, categoryFilter || undefined);
    }
  };

  const handleDeleteClick = (item: Item) => {
    setDeleteConfirm(item);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    try {
      await http.delete(`/items/${deleteConfirm._id}`);
      setDeleteConfirm(null);
      void fetchItems(page, rowsPerPage, categoryFilter || undefined);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete item');
    }
  };

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (dropIndex: number) => {
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      return;
    }
    const reordered = [...items];
    const [removed] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, removed);
    setItems(reordered);
    setDragIndex(null);
    const baseOrder = page * rowsPerPage;
    const updates = reordered.map((item, idx) => ({ id: item._id, order: baseOrder + idx }));
    try {
      await http.patch('/items/reorder', { updates });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reorder');
      void fetchItems(page, rowsPerPage, categoryFilter || undefined);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5">Items</Typography>
          <Typography variant="body2" color="text.secondary">
            Upload and manage showcase pieces. Drag rows to reorder.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleCreate}>
            New Item
          </Button>
        </Box>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Paper sx={{ p: 2 }}>
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell width={48} />
                <TableCell>Title</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow
                  key={item._id}
                  hover
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(index)}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    cursor: 'grab',
                    opacity: dragIndex === index ? 0.6 : 1
                  }}
                >
                  <TableCell sx={{ cursor: 'grab', verticalAlign: 'middle' }}>
                    <DragIndicatorIcon color="action" fontSize="small" />
                  </TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell>{item.slug}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.isActive ? 'Active' : 'Inactive'}
                      color={item.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{new Date(item.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => handleEdit(item)} sx={{ mr: 0.5 }}>
                      Edit
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(item)}
                      aria-label="Delete"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No items yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      <ItemFormDialog open={openDialog} onClose={handleDialogClose} editingItem={editingItem} />

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete item?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete &quot;{deleteConfirm?.title}&quot;? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

